import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Trophy, TrendingUp, TrendingDown, Calendar, Package, RefreshCw, AlertTriangle, Loader2 } from "lucide-react";

/**
 * LifetimePnLCard
 * ---------------------------------------------------------------------------
 * Headline since-launch numbers for the admin dashboard. Mirrors the daily
 * card visually but rendered in a wider 6-cell grid because lifetime has
 * more dimensions worth surfacing (orders count, AOV, first/last paid).
 *
 * Pulls /api/admin/lifetime-pnl and shows:
 *   • Revenue              (Σ total of every paid order, ex. test orders)
 *   • COGS                 (Σ qty × supplier_cost — flagged if incomplete)
 *   • Gross profit
 *   • Ad spend             (Σ daily_metrics.ad_spend across all days logged)
 *   • Net profit / margin  (the headline number)
 *   • Orders count + AOV   (operational context)
 *   • First / last paid    (the date range being aggregated)
 */

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(n || 0));

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit", month: "short", year: "numeric", timeZone: "Europe/London",
    }).format(new Date(iso));
  } catch (_) { return iso; }
};

export default function LifetimePnLCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: pnl } = await api.get("/admin/lifetime-pnl");
      setData(pnl);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load lifetime P&L");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const net = Number(data?.net_profit || 0);
  const profitable = net >= 0;

  return (
    <div className="pg-card p-6 mb-8" data-testid="lifetime-pnl-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Trophy size={22} className="text-[#A6FF00]" />
          <div>
            <div className="pg-micro">// Lifetime Profit &amp; Loss</div>
            <div className="text-[#F7FAFC] pg-display text-xl" style={{ fontFamily: "'Exo 2'" }}>
              Since launch
            </div>
            <div className="text-[#9CA8B8] text-xs pg-mono uppercase tracking-[0.18em] mt-1">
              {data ? `${fmtDate(data.first_paid_at)} → ${fmtDate(data.last_paid_at)}` : "Loading…"}
            </div>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-[#9CA8B8] hover:text-[#5EEAD4] text-xs pg-mono uppercase tracking-[0.18em] flex items-center gap-1.5 disabled:opacity-50"
          data-testid="lifetime-pnl-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-[#9CA8B8] py-8" data-testid="lifetime-pnl-loading">
          <Loader2 size={16} className="animate-spin text-[#5EEAD4]" />
          Crunching every paid order…
        </div>
      )}

      {error && !loading && (
        <div className="p-3 border border-[#FF8A5B]/40 bg-[#FF8A5B]/[0.06] text-[#FF8A5B] rounded-sm text-sm" data-testid="lifetime-pnl-error">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          {/* Headline net profit ribbon */}
          <div
            className="rounded-sm border-l-2 p-4 mb-4 flex items-center justify-between gap-4"
            style={{
              borderLeftColor: profitable ? "#A6FF00" : "#FF8A5B",
              background: profitable ? "rgba(166,255,0,0.04)" : "rgba(255,138,91,0.04)",
            }}
            data-testid="lifetime-pnl-headline"
          >
            <div className="flex items-center gap-3">
              {profitable
                ? <TrendingUp size={26} className="text-[#A6FF00]" />
                : <TrendingDown size={26} className="text-[#FF8A5B]" />}
              <div>
                <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]">
                  {profitable ? "Net profit · in the black" : "Net loss · in the red"}
                </div>
                <div className="pg-display text-3xl text-[#F7FAFC]" style={{ fontFamily: "'Exo 2'" }}>
                  {gbp(Math.abs(net))}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]">Margin</div>
              <div className="pg-display text-xl" style={{ color: profitable ? "#A6FF00" : "#FF8A5B", fontFamily: "'Exo 2'" }}>
                {Number(data.net_margin_pct).toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Operational grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lifetime-pnl-grid">
            <Metric label="Revenue"        value={gbp(data.revenue)}        sub={`${data.orders_count} orders`} accent="#00C2D6" />
            <Metric label="COGS"           value={gbp(data.cogs)}            sub={data.cogs_completeness_pct < 100 ? `⚠ ${Number(data.cogs_completeness_pct).toFixed(0)}% SKUs costed` : "All SKUs costed"} accent="#9CA8B8" />
            <Metric label="Gross Profit"   value={gbp(data.gross_profit)}    sub={data.cogs > 0 ? `${((data.gross_profit / Math.max(data.revenue,1)) * 100).toFixed(1)}% gross margin` : "Add supplier costs for accuracy"} accent="#5EEAD4" />
            <Metric label="Ad Spend"       value={gbp(data.ad_spend)}        sub={`${data.ad_spend_days_logged} day${data.ad_spend_days_logged === 1 ? "" : "s"} logged`} accent="#FF8A5B" />
            <Metric label="Avg Order Value" value={gbp(data.avg_order_value)} sub="Across all paid orders" accent="#A6FF00" icon={Package} />
            <Metric label="Net Profit"     value={gbp(net)}                   sub={`${Number(data.net_margin_pct).toFixed(1)}% margin`} accent={profitable ? "#A6FF00" : "#FF8A5B"} icon={profitable ? TrendingUp : TrendingDown} />
          </div>

          {/* Missing-cost-SKU warning */}
          {data.missing_cost_skus && data.missing_cost_skus.length > 0 && (
            <div className="mt-4 p-3 border border-[#FF8A5B]/30 bg-[#FF8A5B]/[0.04] rounded-sm text-xs text-[#9CA8B8] leading-relaxed flex items-start gap-2" data-testid="lifetime-pnl-missing-costs">
              <AlertTriangle size={14} className="text-[#FF8A5B] shrink-0 mt-0.5" />
              <div>
                <span className="text-[#FF8A5B] pg-mono uppercase tracking-[0.18em] text-[10px]">
                  Incomplete COGS
                </span>{" "}
                — {data.missing_cost_skus.length} SKU{data.missing_cost_skus.length === 1 ? " is" : "s are"} missing a
                <code className="pg-mono text-[#F7FAFC] mx-1">supplier_cost</code>
                so they&apos;re excluded from COGS / gross / net. Add the cost in{" "}
                <strong className="text-[#F7FAFC]">Admin → Products</strong> for an accurate number. Affected:{" "}
                <span className="pg-mono text-[#F7FAFC]">{data.missing_cost_skus.join(", ")}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Metric({ label, value, sub, accent, icon: Icon }) {
  return (
    <div className="p-3 bg-[#0B1220]/40 border border-[#00C2D6]/15 rounded-sm" data-testid={`lifetime-pnl-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">{label}</div>
        {Icon && <Icon size={12} style={{ color: accent }} />}
      </div>
      <div className="pg-display text-xl text-[#F7FAFC]" style={{ fontFamily: "'Exo 2'" }}>{value}</div>
      {sub && <div className="text-[10px] text-[#9CA8B8] mt-1 leading-tight">{sub}</div>}
    </div>
  );
}
